// Settings › Cancellation reasons (wave 3, W2_Portal_Tables look): the
// reasons customers and drivers pick when they cancel a trip, in the
// modern table, with Add / Edit in a dialog (labels above, inline errors, a
// busy Save) instead of material-table's inline rows, and Delete after a
// dialog that names the reason.
//
// Data: the same /cancel_reason list loaded at sign-in (cancelreasondata);
// nothing new is read. Every change writes the whole list with the same
// action as before, editCancellationReason(list, 'Add' | 'Update' |
// 'Delete'), after the same convertLanguage(label, locale) for a new or
// renamed reason. A new reason is { label, value: <list length> }, as before.
// Only an admin reaches this tab; changes need "critical edits" on
// (settings.AllowCriticalEditsAdmin), as before.
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import { api } from 'common';
import { getLangKey } from 'common/src/other/getLangKey';
import { showToast, confirmDialog } from '../components/feedback';
import { DataTable, RecordDialog, PageHeader, calmCaps } from '../components/modern';

// The store keeps the list loaded at sign-in (EDIT_CANCELLATION_REASON does
// not change it), so the list saved in this session is kept here and
// survives switching Settings tabs, as long as the store still holds the
// same sign-in copy (the old page got this by changing the store's array in
// place, which also left deleted reasons on screen).
let savedCache = null; // { source, data }

const toList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value && typeof value === 'object') return Object.values(value).filter(Boolean);
  return [];
};

const listFor = (source) => (savedCache && savedCache.source === source ? savedCache.data : toList(source));

export default function CancellationReasons() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { editCancellationReason, convertLanguage } = api;
  const settings = useSelector((state) => state.settingsdata.settings);
  const cancelreasondata = useSelector((state) => state.cancelreasondata);
  const auth = useSelector((state) => state.auth);
  const source = cancelreasondata.complex;
  const [data, setData] = useState(() => listFor(source));
  const [editing, setEditing] = useState(null); // { row } to edit, {} to add

  useEffect(() => {
    setData(listFor(source));
  }, [source]);

  const canEdit = !!settings.AllowCriticalEditsAdmin;
  const langLocale = auth && auth.profile && auth.profile.lang && auth.profile.lang.langLocale ? auth.profile.lang.langLocale : null;

  const remember = (next) => {
    savedCache = { source, data: next };
    setData(next);
  };

  // A reason as people read it: its translation, or the words as typed when
  // the language pack has none yet.
  const shown = (row) => (row && row.label ? t(getLangKey(row.label), { defaultValue: row.label }) : '');

  const handleSave = async (values, { mode, initialValues }) => {
    const label = values.label;
    if (mode === 'add') {
      const newRow = { label, value: data.length };
      await convertLanguage(label, langLocale);
      const next = data.concat([newRow]);
      dispatch(editCancellationReason(next, 'Add'));
      remember(next);
      showToast.success(t('cancel_reasons_added', { defaultValue: 'Reason added' }));
      return;
    }
    const index = data.indexOf(initialValues);
    if (index < 0) throw new Error(t('cancel_reasons_changed', { defaultValue: 'The list changed. Close this and try again.' }));
    // Nothing to write when the words did not change (the old inline edit
    // wrote the same list again).
    if (label === initialValues.label) return;
    await convertLanguage(label, langLocale);
    const next = data.slice();
    next[index] = { ...initialValues, label };
    dispatch(editCancellationReason(next, 'Update'));
    remember(next);
    showToast.success(t('saved', { defaultValue: 'Saved' }));
  };

  // Removes the chosen reason only. (The old filter on `value` also removed
  // any other reason that shared its number: value is the list length when
  // a reason is added, so a delete followed by an add can repeat it.)
  const handleDelete = async (row) => {
    const ok = await confirmDialog({
      title: t('cancel_reasons_delete_title', { reason: shown(row), defaultValue: 'Delete the reason “{{reason}}”?' }),
      body: t('cancel_reasons_delete_body', { defaultValue: 'Customers and drivers will no longer see it when they cancel a trip.' }),
      confirmLabel: t('delete', { defaultValue: 'Delete' }),
      cancelLabel: t('confirm_cancel', { defaultValue: 'Cancel' }),
      destructive: true,
    });
    if (!ok) return;
    const next = data.filter((item) => item !== row);
    dispatch(editCancellationReason(next, 'Delete'));
    remember(next);
    showToast.success(t('cancel_reasons_deleted', { defaultValue: 'Reason deleted' }));
  };

  const title = calmCaps(t('cancellation_reasons_title', { defaultValue: 'Cancellation reasons' }));
  const columns = [
    {
      title: t('reason', { defaultValue: 'Reason' }),
      field: 'label',
      // Search finds the words on screen and the words as stored.
      searchValue: (row) => `${shown(row)} ${row.label || ''}`,
      sortValue: (row) => shown(row),
      render: (row) => (
        <Box component="span" sx={{ fontWeight: 500 }}>
          {shown(row)}
        </Box>
      ),
    },
  ];

  const actions = canEdit
    ? [
        { key: 'edit', label: t('edit', { defaultValue: 'Edit' }), icon: <EditOutlinedIcon />, inline: true, onClick: (row) => setEditing({ row }) },
        {
          key: 'delete',
          label: t('cancel_reasons_delete', { defaultValue: 'Delete reason' }),
          icon: <DeleteOutlineRoundedIcon />,
          inline: true,
          destructive: true,
          onClick: handleDelete,
        },
      ]
    : [];

  const addLabel = t('cancel_reasons_add', { defaultValue: 'Add reason' });
  const isEdit = !!(editing && editing.row);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        primaryAction={{ label: addLabel, icon: <AddRoundedIcon />, onClick: () => setEditing({}), show: canEdit }}
      />
      <DataTable
        title={title}
        rows={data}
        columns={columns}
        rowKey={(row, index) => `${index}-${row.value}`}
        loading={!!cancelreasondata.loading && !data.length}
        countText={({ shown: n, total, query, loading }) =>
          loading
            ? t('loading', { defaultValue: 'Loading…' })
            : query
              ? t('table_count_shown', { shown: n, total, defaultValue: '{{shown}} of {{total}}' })
              : t('cancel_reasons_count', {
                  count: total,
                  defaultValue_one: '{{count}} reason',
                  defaultValue_other: '{{count}} reasons',
                  defaultValue: '{{count}} reasons',
                })
        }
        rowLabel={shown}
        actions={actions}
        searchPlaceholder={t('cancel_reasons_search', { defaultValue: 'Search reasons' })}
        prefsKey="cancel_reasons"
        exportCsv
        exportPrint
        exportFileName="cancellation_reasons"
        exportTitle={title}
        minWidth={420}
        emptyState={({ query }) =>
          query
            ? null
            : {
                icon: <EventBusyOutlinedIcon />,
                title: t('cancel_reasons_empty', { defaultValue: 'No cancellation reasons yet' }),
                body: t('cancel_reasons_empty_body', { defaultValue: 'Customers and drivers pick one of these when they cancel a trip.' }),
                actionLabel: canEdit ? addLabel : undefined,
                onAction: canEdit ? () => setEditing({}) : undefined,
              }
        }
      />
      <RecordDialog
        open={!!editing}
        mode={isEdit ? 'edit' : 'add'}
        title={isEdit ? t('cancel_reasons_edit', { defaultValue: 'Edit reason' }) : addLabel}
        fields={[
          {
            name: 'label',
            label: t('reason', { defaultValue: 'Reason' }),
            required: true,
            autoFocus: true,
            maxLength: 200,
            // convertLanguage stores the words in your language pack and a
            // machine translation in the others.
            hint: t('cancel_reasons_hint', { defaultValue: 'The other languages get a machine translation when you save.' }),
          },
        ]}
        initialValues={isEdit ? editing.row : undefined}
        onSubmit={handleSave}
        onClose={() => setEditing(null)}
      />
    </Box>
  );
}
